import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import LoginForm from './components/LoginForm';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import HtmlLangUpdater from './components/HtmlLangUpdater';
import './styles/App.css';
import TestSuitePage from './pages/TestSuitePage';
import AdminViewPage from './pages/AdminViewPage';
import { authApi, ApiError } from './services/authApi';
import { authStorage } from './services/authStorage';
import { UserRole } from './types/auth';
import { ToastProvider } from './contexts/ToastContext';

interface AppUser {
  username: string;
  role: UserRole;
}

const App: React.FC = () => {
  return (
    <ToastProvider>
      <Router>
        <AppContent />
      </Router>
    </ToastProvider>
  );
};

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [user, setUser] = useState<AppUser | null>(null);
  const [loginError, setLoginError] = useState<string>('');

  // Check for existing authentication on app load
  useEffect(() => {
    const checkAuthState = () => {
      const authState = authStorage.getAuthState();
      if (authState && authState.isAuthenticated) {
        const userRole = authStorage.getUserRole();
        const userData = authStorage.getUserData();
        
        if (userData) {
          setUser({
            username: userData.username,
            role: userRole,
          });
          setLoggedIn(true);
        }
      }
    };

    checkAuthState();
  }, []);

  const handleLogin = async (email: string, password: string) => {
    setIsLoading(true);
    setLoginError('');
    
    try {
      const response = await authApi.login({ email, password });
      
      // Save authentication data to encrypted storage
      authStorage.saveAuthState(response);
      
      // Determine user role based on response
      const userRole: UserRole = response.user.companyId === null ? 'admin' : 'user';
      
      setUser({
        username: response.user.username,
        role: userRole,
      });
      setLoggedIn(true);
      
    } catch (error) {
      console.error('Login failed:', error);
      
      if (error instanceof ApiError) {
        setLoginError(error.message);
      } else {
        setLoginError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    authStorage.clearAuthState();
    setLoggedIn(false);
    setUser(null);
    setLoginError('');
    // Navigate to root path instead of letting routing redirect to /login
    navigate('/', { replace: true });
  };

  // Auto-logout when the JWT expires (1 hour after login)
  useEffect(() => {
    if (!loggedIn) return;
    const authState = authStorage.getAuthState();
    if (!authState?.expiresAt) return;
    const remaining = authState.expiresAt - Date.now();
    if (remaining <= 0) { handleLogout(); return; }
    const timer = setTimeout(() => { handleLogout(); }, remaining);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  // Render different views based on user role
  const renderUserView = () => {
    if (!user) return null;

    if (user.role === 'admin') {
      return <AdminViewPage onLogout={handleLogout} currentUser={user} />;
    } else {
      return <TestSuitePage onLogout={handleLogout} currentUser={user} />;
    }
  };

  // Update HTML lang attribute when language changes
  return (
    <div className="app">
      <HtmlLangUpdater />
      
      <Routes>
        <Route 
          path="/login" 
          element={<Navigate to="/" replace />}
        />
        <Route 
          path="/forgot-password" 
          element={
            loggedIn && user ? (
              <Navigate to="/" replace />
            ) : (
              <ForgotPassword />
            )
          } 
        />
        <Route 
          path="/reset-password" 
          element={
            loggedIn && user ? (
              <Navigate to="/" replace />
            ) : (
              <ResetPassword />
            )
          } 
        />
        <Route 
          path="/verify" 
          element={
            loggedIn && user ? (
              <Navigate to="/" replace />
            ) : (
              <ResetPassword />
            )
          } 
        />
        <Route 
          path="/" 
          element={
            loggedIn && user ? (
              renderUserView()
            ) : (
              <LoginForm 
                onSubmit={handleLogin} 
                isLoading={isLoading} 
                error={loginError}
              />
            )
          } 
        />
      </Routes>
    </div>
  );
};

export default App;